import { Share2, TrendingUp, Dumbbell, Sword, HeartHandshake } from 'lucide-react';
import { Button } from '../ui/button';
import Avatar from '../ui/avatar-initials';
import { shareText, firstName } from '../../lib/play';
import CardActions from './CardActions';

const MEDALS = ['🥇', '🥈', '🥉'];

export default function SeasonWrappedCard({ wrapped, groupName, groupId, isAdmin }) {
  const { season, awards } = wrapped;
  const podium = awards?.podium || [];

  const share = () => {
    const lines = [`🏆 ${groupName} · ${season.name} Wrapped`, ''];
    podium.forEach((p, i) => lines.push(`${MEDALS[i]} ${p.name} — ${p.rating} (${p.wins}W-${p.losses}L)`));
    if (awards.most_improved) lines.push(`📈 Most improved: ${awards.most_improved.name} (+${awards.most_improved.gain})`);
    if (awards.iron_man) lines.push(`🏋️ Iron Man: ${awards.iron_man.name} (${awards.iron_man.matches} games)`);
    if (awards.giant_slayer) lines.push(`🗡️ Giant Slayer: ${awards.giant_slayer.name} (${awards.giant_slayer.wins})`);
    if (awards.best_duo) lines.push(`🤝 Best duo: ${awards.best_duo.names.join(' & ')} (${awards.best_duo.wins}/${awards.best_duo.played})`);
    lines.push('', `${awards.total_matches} matches · ${awards.total_players} players · via SplitEasy`);
    shareText(lines.join('\n'));
  };

  return (
    <div className="rounded-3xl bg-slate-950 text-white p-5 relative overflow-hidden" data-testid="season-wrapped-card">
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-amber-500/20 blur-3xl" />
      <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-bold">{groupName} · Season wrapped</p>
      <h2 className="font-display font-black text-2xl mt-1">{season.name}</h2>
      <p className="text-xs text-white/60">{season.starts_at} → {(season.ended_at || '').slice(0, 10) || 'now'} · {awards.total_matches} matches · {awards.total_players} players</p>

      {podium.length === 0 ? (
        <p className="mt-6 text-sm text-white/70" data-testid="wrapped-empty">No matches were played this season.</p>
      ) : (
        <div className="mt-5 flex items-end justify-center gap-3" data-testid="wrapped-podium">
          {[1, 0, 2].map((i) => podium[i] && (
            <div key={podium[i].user_id} className="flex flex-col items-center" style={{ order: i === 0 ? 1 : i === 1 ? 0 : 2 }}>
              <Avatar name={podium[i].name} size={i === 0 ? 'lg' : 'md'} className="ring-2 ring-amber-400/60" />
              <span className="text-xl mt-1">{MEDALS[i]}</span>
              <span className="text-xs font-bold">{firstName(podium[i].name)}</span>
              <span className="text-xs text-white/60 tabular-nums">{podium[i].rating}</span>
              <div className={`mt-1 w-16 rounded-t-lg bg-amber-500/30 ${i === 0 ? 'h-14' : i === 1 ? 'h-9' : 'h-6'}`} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-2 text-xs">
        {awards.most_improved && <Award icon={<TrendingUp className="w-3.5 h-3.5" />} label="Most improved" value={`${firstName(awards.most_improved.name)} +${awards.most_improved.gain}`} />}
        {awards.iron_man && <Award icon={<Dumbbell className="w-3.5 h-3.5" />} label="Iron Man" value={`${firstName(awards.iron_man.name)} · ${awards.iron_man.matches} games`} />}
        {awards.giant_slayer && <Award icon={<Sword className="w-3.5 h-3.5" />} label="Giant Slayer" value={`${firstName(awards.giant_slayer.name)} · ${awards.giant_slayer.wins}×`} />}
        {awards.best_duo && <Award icon={<HeartHandshake className="w-3.5 h-3.5" />} label="Best duo" value={`${awards.best_duo.names.map(firstName).join(' & ')} · ${awards.best_duo.wins}/${awards.best_duo.played}`} />}
      </div>

      <Button className="rounded-full w-full mt-5 bg-amber-400 text-slate-950 hover:bg-amber-300" onClick={share} data-testid="wrapped-share-btn">
        <Share2 className="w-4 h-4 mr-1.5" /> Share as text
      </Button>
      {groupId && (
        <div className="mt-3 [&_button]:border-white/20 [&_button]:text-white">
          <CardActions
            buildPath={`/groups/${groupId}/seasons/${season.id}/cards/wrapped`}
            sendPath={`/groups/${groupId}/seasons/${season.id}/cards/wrapped/send`}
            canSend={isAdmin}
            filename={`${season.name}-wrapped.png`}
            shareText={`${groupName} · ${season.name} Wrapped`}
          />
        </div>
      )}
    </div>
  );
}

function Award({ icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/5 p-2.5">
      <p className="text-xs uppercase tracking-wider text-white/50 inline-flex items-center gap-1">{icon}{label}</p>
      <p className="font-semibold truncate">{value}</p>
    </div>
  );
}
